/**
 * Central API client for the Cloudflare Pages Functions backend.
 *
 * Every request automatically attaches the current user's Firebase ID token
 * as a Bearer token in the Authorization header.
 *
 * Endpoint convention: /api/collections/:collection[/:id]
 * OTP:     /api/auth/otp/request   /api/auth/otp/verify
 * Storage: /api/storage/upload     /api/storage/signed-url
 * Proxied functions: /api/functions/:name
 */

import { auth, getAppCheckToken } from '@/lib/firebase';

// ─────────────────────────────────────────────────────────────────────────────
// Token helper
// ─────────────────────────────────────────────────────────────────────────────

async function getIdToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken();
}

// ─────────────────────────────────────────────────────────────────────────────
// Error classes
// ─────────────────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/** Thrown when the network request itself fails (e.g. device is offline). */
export class NetworkError extends Error {
  constructor(message = 'Network request failed — check your connection') {
    super(message);
    this.name = 'NetworkError';
  }
}

/**
 * Produce a user-facing error description for a failed mutation.
 * @param error  The caught value
 * @param context  Short verb phrase describing what failed, e.g. "save event"
 */
export function formatApiError(error: unknown, context: string): string {
  if (error instanceof NetworkError) {
    return `Could not ${context} — network unavailable, check your connection`;
  }
  if (error instanceof ApiError) {
    return `Could not ${context} — server returned ${error.status}`;
  }
  if (error instanceof Error && error.message) {
    return `Could not ${context} — ${error.message}`;
  }
  return `Could not ${context} — unexpected error`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Core fetch wrapper
// ─────────────────────────────────────────────────────────────────────────────

async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
  requireAuth = true,
): Promise<T> {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> | undefined),
  };

  if (requireAuth) {
    const token = await getIdToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  if (
    options.body &&
    typeof options.body === 'string' &&
    !headers['Content-Type']
  ) {
    headers['Content-Type'] = 'application/json';
  }

  let res: Response;
  try {
    res = await fetch(path, { ...options, headers });
  } catch {
    throw new NetworkError();
  }

  if (!res.ok) {
    let message = `API error ${res.status}`;
    try {
      const body = await res.json();
      message = body.error || message;
    } catch {
      // ignore parse error
    }
    throw new ApiError(message, res.status);
  }

  return res.json() as Promise<T>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public helpers
// ─────────────────────────────────────────────────────────────────────────────

export async function apiGet<T = unknown>(
  path: string,
  params?: Record<string, string>,
): Promise<T> {
  const url = params
    ? `${path}?${new URLSearchParams(params).toString()}`
    : path;
  return apiFetch<T>(url, { method: 'GET' });
}

export async function apiPost<T = unknown>(
  path: string,
  body: unknown,
  requireAuth = true,
): Promise<T> {
  return apiFetch<T>(
    path,
    { method: 'POST', body: JSON.stringify(body) },
    requireAuth,
  );
}

export async function apiPut<T = unknown>(
  path: string,
  body: unknown,
): Promise<T> {
  return apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) });
}

export async function apiDelete<T = unknown>(path: string): Promise<T> {
  return apiFetch<T>(path, { method: 'DELETE' });
}

/**
 * Ask the Worker for the correct storage path and bucket for a given upload type.
 * The caller then uploads the binary directly to Firebase Storage.
 */
export async function apiGetStoragePath(
  uploadType: string,
  fileExtension: string,
  entityId?: string,
): Promise<{ path: string; bucket: string }> {
  return apiPost<{ path: string; bucket: string }>('/api/storage/path', {
    uploadType,
    fileExtension,
    entityId: entityId ?? null,
  });
}

/**
 * Upload a file to Firebase Storage directly from the browser (no Worker buffering).
 *
 * Flow:
 *   1. POST /api/storage/path  → { path, bucket }   (Worker decides the path)
 *   2. PUT  to Firebase Storage REST API              (browser → Firebase, no Worker)
 *   3. Return the authenticated download URL with token
 *
 * Using the Firebase Storage REST API with the user's ID token means App Check
 * (reCAPTCHA v3) and Firebase Auth are both satisfied automatically.
 */
export async function apiUploadFile(
  file: File,
  uploadType: string,
  entityId?: string,
): Promise<{ url: string }> {
  // 1. Get the storage path from the Worker
  const ext = (file.name || '').split('.').pop() || 'bin';
  const { path, bucket } = await apiGetStoragePath(uploadType, ext, entityId);

  // 2. Upload directly to Firebase Storage REST API
  const [token, appCheckToken] = await Promise.all([getIdToken(), getAppCheckToken()]);
  if (!token) throw new ApiError('Not authenticated', 401);

  const uploadUrl =
    `https://firebasestorage.googleapis.com/v0/b/${bucket}/o` +
    `?uploadType=media&name=${encodeURIComponent(path)}`;

  const uploadHeaders: Record<string, string> = {
    'Content-Type': file.type || 'application/octet-stream',
    'Authorization': `Bearer ${token}`,
  };
  if (appCheckToken) uploadHeaders['X-Firebase-AppCheck'] = appCheckToken;

  let uploadRes: Response;
  try {
    uploadRes = await fetch(uploadUrl, {
      method: 'POST',
      headers: uploadHeaders,
      body: file,
    });
  } catch {
    throw new NetworkError();
  }

  if (!uploadRes.ok) {
    let message = `Storage upload failed ${uploadRes.status}`;
    try {
      const body = await uploadRes.json();
      message = body.error?.message || body.error || message;
    } catch {}
    throw new ApiError(message, uploadRes.status);
  }

  // 3. Build download URL from the returned metadata
  const metadata = await uploadRes.json() as { downloadTokens?: string; name?: string };
  const downloadToken = (metadata.downloadTokens || '').split(',')[0];
  // Firebase Storage REST API requires slashes in the object name to be encoded
  // as %2F in the URL path — literal / is treated as a path separator and will
  // result in a 404 for objects stored at multi-segment paths like images/series/x.jpg
  const encodedPath = path.split('/').map(encodeURIComponent).join('%2F');

  const url = downloadToken
    ? `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media&token=${downloadToken}`
    : `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;

  return { url };
}

// ─────────────────────────────────────────────────────────────────────────────
// Typed collection helpers
// ─────────────────────────────────────────────────────────────────────────────

export function collectionUrl(
  col: string,
  id?: string,
  sub?: string,
): string {
  let url = `/api/collections/${col}`;
  if (id) url += `/${id}`;
  if (sub) url += `/${sub}`;
  return url;
}

/** List all documents in a collection. Optional single-field filter. */
export function listDocs<T>(
  collection: string,
  filter?: { field: string; value: string; op?: string },
): Promise<T[]> {
  const params: Record<string, string> = {};
  if (filter) {
    params.field = filter.field;
    params.value = filter.value;
    params.op = filter.op ?? '==';
  }
  return apiGet<T[]>(collectionUrl(collection), params);
}

/** Get a single document. */
export function getDoc<T>(collection: string, id: string): Promise<T | null> {
  return apiGet<T | null>(collectionUrl(collection, id));
}

/** Create a document (auto-ID). Returns the new doc with `id`. */
export function createDoc<T>(collection: string, data: unknown): Promise<T> {
  return apiPost<T>(collectionUrl(collection), data);
}

/** Create/overwrite a document with a specific ID. */
export function setDocById<T>(
  collection: string,
  id: string,
  data: unknown,
): Promise<T> {
  return apiPost<T>(collectionUrl(collection, id), data);
}

/** Update an existing document (merge). */
export function updateDoc<T>(
  collection: string,
  id: string,
  data: unknown,
): Promise<T> {
  return apiPut<T>(collectionUrl(collection, id), data);
}

/** Delete a document. */
export function deleteDoc(collection: string, id: string): Promise<unknown> {
  return apiDelete(collectionUrl(collection, id));
}

/** Cascade update (category or contributor). */
export function cascadeUpdate<T>(
  collection: string,
  id: string,
  data: unknown,
): Promise<T> {
  return apiPost<T>(collectionUrl(collection, id, 'cascade'), data);
}

/** Call a proxied Firebase Callable Function via the backend. */
export function callFunction<T>(name: string, data: unknown): Promise<T> {
  return apiPost<T>(`/api/functions/${name}`, data);
}
